import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider } from "@/lib/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import { useTrackPageViews } from "@/lib/analytics";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";

import CustomerLayout from "@/pages/customer/Layout";
import CustomerHome from "@/pages/customer/Home";
import ServiceDetail from "@/pages/customer/ServiceDetail";
import MyBookings from "@/pages/customer/MyBookings";
import BookingDetail from "@/pages/customer/BookingDetail";
import Wallet from "@/pages/customer/Wallet";
import Profile from "@/pages/customer/Profile";
import Tickets from "@/pages/customer/Tickets";
import Membership from "@/pages/customer/Membership";
import Refer from "@/pages/customer/Refer";
import Cart from "@/pages/customer/Cart";

import TechnicianLayout from "@/pages/technician/Layout";
import TechJobs from "@/pages/technician/Jobs";
import TechJobDetail from "@/pages/technician/JobDetail";
import TechEarnings from "@/pages/technician/Earnings";
import TechProfile from "@/pages/technician/Profile";

import AdminLayout from "@/pages/admin/Layout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminBookings from "@/pages/admin/Bookings";
import AdminTechnicians from "@/pages/admin/Technicians";
import AdminCustomers from "@/pages/admin/Customers";
import AdminServices from "@/pages/admin/Services";
import AdminCoupons from "@/pages/admin/Coupons";
import AdminPayouts from "@/pages/admin/Payouts";
import AdminTickets from "@/pages/admin/Tickets";
import AdminPGBooks from "@/pages/admin/PGBooks";
import AdminMemberships from "@/pages/admin/Memberships";
import AdminPayments from "@/pages/admin/Payments";
import AdminUsers from "@/pages/admin/Users";
import AdminRoles from "@/pages/admin/Roles";
import AdminPlatform from "@/pages/admin/Platform";
import AdminVisitors from "@/pages/admin/Visitors";
import AdminEnquiries from "@/pages/admin/Enquiries";
import AdminParts from "@/pages/admin/Parts";
import BillingPage from "@/pages/admin/BillingPage";
import BillingSettings from "@/pages/admin/BillingSettings";
import Contact from "@/pages/Contact";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import ChangePassword from "@/pages/ChangePassword";

const CUSTOMER_ROLES = ["customer"];
const TECH_ROLES = ["technician", "supervisor_technician"];
const ADMIN_ROLES = [
    "super_admin",
    "company_admin",
    "operations_manager",
    "finance_manager",
    "support_agent",
    "service_manager",
    "field_supervisor",
    "auditor",
];

export default function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Toaster
                    position="top-right"
                    toastOptions={{
                        style: {
                            border: "1px solid #E5E2DC",
                            fontFamily: "Outfit, sans-serif",
                        },
                    }}
                />
                <AppRoutes />
            </BrowserRouter>
        </AuthProvider>
    );
}

function AppRoutes() {
    useTrackPageViews();
    return (
        <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/change-password" element={<ChangePassword />} />
                    <Route path="/payment/success" element={<PaymentSuccess />} />
                    <Route path="/payment/cancel" element={<PaymentCancel />} />

                    {/* Customer — /customer + /customer/services/:id are public browse;
                        transactional routes below still require auth. */}
                    <Route path="/customer" element={<CustomerLayout />}>
                        <Route index element={<CustomerHome />} />
                        <Route path="services/:id" element={<ServiceDetail />} />

                        <Route
                            path="bookings"
                            element={
                                <ProtectedRoute roles={CUSTOMER_ROLES}>
                                    <MyBookings />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="bookings/:id"
                            element={
                                <ProtectedRoute roles={[...CUSTOMER_ROLES, "super_admin", "admin", "company_admin", "operations", "finance", "manager"]}>
                                    <BookingDetail />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="cart"
                            element={
                                <ProtectedRoute roles={CUSTOMER_ROLES}>
                                    <Cart />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="wallet"
                            element={
                                <ProtectedRoute roles={CUSTOMER_ROLES}>
                                    <Wallet />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="membership"
                            element={
                                <ProtectedRoute roles={CUSTOMER_ROLES}>
                                    <Membership />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="refer"
                            element={
                                <ProtectedRoute roles={CUSTOMER_ROLES}>
                                    <Refer />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="tickets"
                            element={
                                <ProtectedRoute roles={CUSTOMER_ROLES}>
                                    <Tickets />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="profile"
                            element={
                                <ProtectedRoute roles={CUSTOMER_ROLES}>
                                    <Profile />
                                </ProtectedRoute>
                            }
                        />
                    </Route>

                    {/* Technician */}
                    <Route
                        path="/technician"
                        element={
                            <ProtectedRoute roles={TECH_ROLES}>
                                <TechnicianLayout />
                            </ProtectedRoute>
                        }
                    >
                        <Route index element={<TechJobs />} />
                        <Route path="jobs/:id" element={<TechJobDetail />} />
                        <Route path="earnings" element={<TechEarnings />} />
                        <Route path="profile" element={<TechProfile />} />
                    </Route>

                    {/* Admin */}
                    <Route
                        path="/admin"
                        element={
                            <ProtectedRoute roles={ADMIN_ROLES}>
                                <AdminLayout />
                            </ProtectedRoute>
                        }
                    >
                        <Route index element={<AdminDashboard />} />
                        <Route path="bookings" element={<AdminBookings />} />
                        <Route path="technicians" element={<AdminTechnicians />} />
                        <Route path="customers" element={<AdminCustomers />} />
                        <Route path="services" element={<AdminServices />} />
                        <Route path="coupons" element={<AdminCoupons />} />
                        <Route path="payouts" element={<AdminPayouts />} />
                        <Route path="tickets" element={<AdminTickets />} />
                        <Route path="pgbooks" element={<AdminPGBooks />} />
                        <Route path="memberships" element={<AdminMemberships />} />
                        <Route path="payments" element={<AdminPayments />} />
                        <Route path="users" element={<AdminUsers />} />
                        <Route path="roles" element={<AdminRoles />} />
                        <Route path="platform" element={<AdminPlatform />} />
                        <Route path="visitors" element={<AdminVisitors />} />
                        <Route path="enquiries" element={<AdminEnquiries />} />
                        <Route path="parts" element={<AdminParts />} />
                        <Route path="quotations" element={<BillingPage kind="quotation" />} />
                        <Route path="tax-invoices" element={<BillingPage kind="invoice" />} />
                        <Route path="settings/billing" element={<BillingSettings />} />
                    </Route>

                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
    );
}

// Inner component (lives under <BrowserRouter>) so useLocation() works.
// Placeholder kept in case we ever need it; currently the page-view hook is
// invoked from each Layout component or the customer routes.
